import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import AvisoNovaVersao from './components/AvisoNovaVersao';
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import EsqueciSenha from './pages/EsqueciSenha';
import ResetSenha from './pages/ResetSenha';

// Lazy-loaded pages (code splitting)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const VisaoGeral = lazy(() => import('./pages/VisaoGeral'));
const Pesquisar = lazy(() => import('./pages/Pesquisar'));
const Radar = lazy(() => import('./pages/Radar'));
const MinhasLicitacoes = lazy(() => import('./pages/MinhasLicitacoes'));
const Arquivo = lazy(() => import('./pages/Arquivo'));
const DetalheLicitacao = lazy(() => import('./pages/DetalheLicitacao'));
const Documentos = lazy(() => import('./pages/Documentos'));
const Agenda = lazy(() => import('./pages/Agenda'));
const IAStudio = lazy(() => import('./pages/IAStudio'));
const DisputaLances = lazy(() => import('./pages/DisputaLances'));
const Concorrente = lazy(() => import('./pages/Concorrente'));
const PCA = lazy(() => import('./pages/PCA'));
const Terceirizados = lazy(() => import('./pages/Terceirizados'));
const RecursoAutomatico = lazy(() => import('./pages/RecursoAutomatico'));
const Financeiro = lazy(() => import('./pages/Financeiro'));
const Workflow = lazy(() => import('./pages/Workflow'));
const Empenhos = lazy(() => import('./pages/Empenhos'));
const Participacoes = lazy(() => import('./pages/Participacoes'));
const Monitoramento = lazy(() => import('./pages/Monitoramento'));
const Perfil = lazy(() => import('./pages/Perfil'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const PesquisaPrecos = lazy(() => import('./pages/PesquisaPrecos'));
const MinhaEmpresa = lazy(() => import('./pages/MinhaEmpresa'));

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '4px solid #e0f0e7',
        borderTopColor: '#00874a',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: 18,
        color: '#00874a',
      }}>
        Carregando...
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {/* [2026-08-12] Avisa quando existe versao nova. Sem isso o Erivan ficou horas
          com a pagina antiga aberta e a IA parecia quebrada. */}
      <AvisoNovaVersao />
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route path="/cadastro" element={user ? <Navigate to="/" /> : <Cadastro />} />
        <Route path="/esqueci-senha" element={user ? <Navigate to="/" /> : <EsqueciSenha />} />
        <Route path="/reset-senha" element={<ResetSenha />} />
        <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="visao-geral" element={<VisaoGeral />} />
          <Route path="licitacao/:id" element={<DetalheLicitacao />} />
          <Route path="pesquisar" element={<Pesquisar />} />
          <Route path="radar" element={<Radar />} />
          <Route path="minhas" element={<MinhasLicitacoes />} />
          <Route path="arquivo" element={<Arquivo />} />
          <Route path="agenda" element={<Agenda />} />
          <Route path="empenhos" element={<Empenhos />} />
          <Route path="participacoes" element={<Participacoes />} />
          <Route path="disputa" element={<DisputaLances />} />
          {/* Mensagens depreciada — redireciona pro Monitoramento que cobre o caso de uso */}
          <Route path="mensagens" element={<Navigate to="/monitoramento" replace />} />
          <Route path="monitoramento" element={<Monitoramento />} />
          <Route path="concorrente" element={<Concorrente />} />
          <Route path="pesquisa-precos" element={<PesquisaPrecos />} />
          <Route path="minha-empresa" element={<MinhaEmpresa />} />
          <Route path="pca" element={<PCA />} />
          <Route path="terceirizados" element={<Terceirizados />} />
          <Route path="recurso-automatico" element={<RecursoAutomatico />} />
          <Route path="ia-studio" element={<IAStudio />} />
          <Route path="financeiro" element={<Financeiro />} />
          <Route path="documentos" element={<Documentos />} />
          <Route path="workflow" element={<Workflow />} />
          <Route path="configuracoes" element={<Configuracoes />} />
          <Route path="perfil" element={<Perfil />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

export default App;
